import { useTranslations } from "next-intl";

import { PlotlyRenderer } from "@/components/plotly/PlotlyRenderer";

import { isChartResult } from "./dataAnalysisUtils";

import type { AnalysisResult, ChartResult, DescriptiveResult } from "./dataAnalysisUtils";

type Props = {
  result: AnalysisResult;
};

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

function DescriptiveTable({ data }: { data: DescriptiveResult }) {
  const t = useTranslations("sidebar.activeNode.dataAnalysis.results");

  const rows: [string, number][] = [
    [t("count"), data.count],
    [t("mean"), data.mean],
    [t("median"), data.median],
    [t("std"), data.std],
    [t("variance"), data.variance],
    [t("min"), data.min],
    [t("q1"), data.q1],
    [t("q3"), data.q3],
    [t("iqr"), data.iqr],
    [t("max"), data.max],
  ];

  return <StatsTable rows={rows} />;
}

function ChartPreview({ data }: { data: ChartResult }) {
  const t = useTranslations("sidebar.activeNode.dataAnalysis");

  const previewLayout = {
    ...data.plotlyLayout,
    margin: { l: 35, r: 15, t: 20, b: 35 },
  } as Partial<Plotly.Layout>;

  return (
    <div>
      <span className="font-mono text-xxxs cursor-text select-none uppercase tracking-tight text-gray-500 pl-1">
        {t("chart")}
      </span>
      <div className="relative mb-2 shadow-block dark:shadow-block-inverted overflow-hidden rounded-6 h-[200px]">
        <PlotlyRenderer
          data={data.plotlyData as Plotly.Data[]}
          layout={previewLayout}
          width={280}
          height={200}
        />
      </div>
    </div>
  );
}

function StatsTable({ rows }: { rows: [string, number][] }) {
  return (
    <div className="flex flex-col gap-0.5">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="flex items-center justify-between px-1 py-0.5 text-xxs font-mono tracking-tight"
        >
          <span className="text-gray-500 uppercase">{label}</span>
          <span className="text-gray-900 dark:text-gray-100 tabular-nums">
            {formatNumber(value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function DataAnalysisResult({ result }: Props) {
  if (isChartResult(result)) {
    return <ChartPreview data={result.data} />;
  }

  switch (result.type) {
    case "descriptive":
      return <DescriptiveTable data={result.data} />;
    default:
      return result satisfies never;
  }
}
